import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { getLastDayOfCurrentMonth } from 'src/app/core/utils/date.utils';
import { errorMessageHandler } from 'src/app/core/utils/error-handler.utils';
import { showMatSnackMessage } from 'src/app/core/utils/message-handler.util';
import { conceptosDto, DatosFacturaCompleta, DatosFormularioFactura } from 'src/app/models/carga-fc.dto';
import { MonedasDto } from 'src/app/models/monedas.dto';
import { ComproCabeProveService } from 'src/app/services/compro-cabe-prove.service';
import { MonedasService } from 'src/app/services/monedas.service';

@Component({
  selector: 'app-carga-facturas',
  templateUrl: './carga-facturas.component.html',
  styleUrls: ['./carga-facturas.component.scss']
})
export class CargaFacturasComponent implements OnInit {
 
  registros: conceptosDto[] = [];
  monedas: MonedasDto[] = [];
  selectedOrderIds = new Set<string>();
  totalSelected = 0;
  monedaSeleccionada: string = 'PES';
  miniLoader: boolean = false;

  toDate: string = getLastDayOfCurrentMonth(); // fin del mes en curso 
  fromDate: string = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // una semana antes de ayer
  toDateMax: string = getLastDayOfCurrentMonth();  
 

  constructor(private comproCabeProveService: ComproCabeProveService,
    private monedasService: MonedasService,
    private router: Router,
    private snackBar: MatSnackBar) { }

  ngOnInit(): void {
    this.monedasService.getAll().subscribe(data => this.monedas = data);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.consultar();
    }, 1000);
  }


  consultar() {

    this.activarLoader();

    this.comproCabeProveService.getOCPendientes({ fecha_desde: this.fromDate, fecha_hasta: this.toDate, moneda: this.monedaSeleccionada }).subscribe({
      next: (response: conceptosDto[]) => {
        this.procesarResponseConsultar(response);
      },
      error: (error: any) => {
        errorMessageHandler(error);
        this.desactivarLoader();
      },
      complete: () => {
        this.desactivarLoader();
      },
    });
  }

  procesarResponseConsultar(response: conceptosDto[]) {

    this.registros = [];

    if ((Array.isArray(response) && response.length === 0) || !response) {
      showMatSnackMessage(this.snackBar, 'Sin resultados', 5000, 'snackbar-custom-orange');
      return;
    } 

    this.registros = response;

  }

  // Obtiene una clave única para la orden
  getOrderKey(order: conceptosDto): string {
    return `${order.TIPO}-${order.NUM}-${order.renglon}`;
  }

  // Maneja la selección de una fila individual
  toggleOrderSelection(order: conceptosDto): void {
    const key = this.getOrderKey(order);
    if (this.selectedOrderIds.has(key)) {
      this.selectedOrderIds.delete(key);
    } else {
      this.selectedOrderIds.add(key);
    }
    this.calculateTotal();
  }

  // Maneja la selección de "todas las filas"
  toggleSelectAll(): void {
    if (this.selectedOrderIds.size === this.registros.length) {
      this.selectedOrderIds.clear();
    } else {
      this.registros.forEach(order => this.selectedOrderIds.add(this.getOrderKey(order)));
    }
    this.calculateTotal();
  }

  // Verifica si todas las filas están seleccionadas (para el checkbox del header)
  isAllSelected(): boolean {
    return this.registros.length > 0 && this.selectedOrderIds.size === this.registros.length;
  }

  // Calcula el total de los importes pendientes seleccionados
  calculateTotal(): void {
    this.totalSelected = this.registros
      .filter(order => this.selectedOrderIds.has(this.getOrderKey(order)))
      .reduce((sum, order) => sum + (order.impor_pend || 0), 0);
  }

  // Genera la factura a partir de los seleccionados
  generateInvoice(): void {
    if (this.selectedOrderIds.size === 0) {
      showMatSnackMessage(this.snackBar, 'Por favor seleccione al menos una orden de compra', 5000, 'snackbar-custom-orange');
      return;
    }

    this.activarLoader();

    let conceptos = this.registros.filter(order => this.selectedOrderIds.has(this.getOrderKey(order)));

    let datos: DatosFacturaCompleta = {
      formulario: this.armarFormularioInicialDesdeOrden(conceptos[0]),
      conceptos: conceptos,
      articulos: []
    };

    datos = this.setImportesEditablesConceptos(datos);

    this.comproCabeProveService.completarDatosConceptos(datos).subscribe({
      next: (datosCompletos) => {
        this.comproCabeProveService.setDatosFactura(datosCompletos);
        this.desactivarLoader();
        this.router.navigate(['home/proveedores/nueva-fc']);
      },
      error: (error) => {
        this.desactivarLoader();
        console.error('Error completando datos conceptos', error);
        showMatSnackMessage(this.snackBar, 'Error al completar los datos. Intente nuevamente.', 5000, 'snackbar-custom-red');
      }
    });

  }


  armarFormularioInicialDesdeOrden(orden: conceptosDto): DatosFormularioFactura {
    return {
      datosBasicos: {
        tipo: null,
        numero: null,
        sucursal: null,
        codigoProveedor: null,
        nombreProveedor: localStorage.getItem('nombre') ?? '',
        moneda: orden.MONE,
        cotizacion: null,
        provincia: null,
        condicionPago: null,
        remito: null,
        codigoOperacion: null,
        compGeneraAsiento: null
      },
      fechas: {
        fechaEmision: new Date().toISOString().split('T')[0],
        fechaVencimiento: new Date().toISOString().split('T')[0],
        fechaPago: new Date().toISOString().split('T')[0],
        fechaPresentacion: new Date().toISOString().split('T')[0],
        fechaRegistro: new Date().toISOString().split('T')[0]
      },
      totales: {
        porcentajePercepcionIVA: 0,
        importePercepcionIVA: 0,
        provinciaPercepcionIVA: null,
        porcentajePercepcionIB: 0,
        importePercepcionIB: 0,
        provinciaPercepcionIB: null,
        total: this.totalSelected,
        porcentajePercepcionIB2: 0,
        importePercepcionIB2: 0,
        provinciaPercepcionIB2: null,
        porcentajePercepcionIBPlus: 0,
        importePercepcionIBPlus: 0,
        porcentajePercepcionGcias: 0,
        importePercepcionGcias: 0,
        porcentajeImpuestosInternos: 0,
        importeImpuestosInternos: 0,
        iva1: {
          porcentaje: 0,
          base: 0,
          importe: 0
        },
        iva2: {
          porcentaje: 0,
          base: 0,
          importe: 0
        },
        iva3: {
          porcentaje: 0,
          base: 0,
          importe: 0
        },
        iva4: {
          porcentaje: 0,
          base: 0,
          importe: 0
        },
        subtotalExento: 0,
        subtotalGravado: 0,
        totalBase: 0,
        importeBaseImpuestosInternos: 0
      },
      archivo: {
        binario: null,
        nombre: null,
        limiteSubida: 5
      }
    };
  }

  setImportesEditablesConceptos(datos: DatosFacturaCompleta): DatosFacturaCompleta {
    if (datos) {
      datos.conceptos.forEach(c => {
        c.impor_editable = c.impor;
        c.porcen_iva_editable = c.porcen_iva;
      });
    }
    return datos;
  }

  onCurrencyChange($event: any) {
  this.registros = [];
  }

  activarLoader(): void {
    this.miniLoader = true;
  }

  desactivarLoader(): void {
    setTimeout(() => {
      this.miniLoader = false;
    }, 1000);
  }

}
